/**
 * What the dinner-claim API answered, turned into something a host can act on.
 *
 * This exists because `mutationErrorMessage` cannot do it. Every refusal on this
 * endpoint is a `DinnerClaimResult` body — `{ status, availableSpots,
 * conflictVenueName, maxPartySize, ... }` — and that shape carries none of the
 * `error` / `detail` / `title` / `message` keys the shared helper looks for. So
 * the shared helper falls through to the transport string and a host who just
 * tried to seat someone reads "Request failed with status code 409".
 *
 * Which is the bug that shipped to guests: a refusal that names a venue,
 * rendered as a generic "that didn't save". The one that matters most is
 * `dinner_conflict` — the guest already holds a seat somewhere else that
 * evening, the payload says WHERE, and a host who is not told where has no move
 * left except to open every other hold and read the guest lists.
 *
 * Nothing here decides anything. The server owns the capacity rule, the party
 * cap and the one-table-per-evening rule; this file only reads its answer out
 * loud. That is deliberate — a second copy of those rules living in Studio is a
 * second copy that can disagree.
 *
 * What it does have to know is the SHAPE OF THE HOST'S POWERS, because a refusal
 * that names an action the reader cannot take is the same dead end as a refusal
 * that names nothing — and that shape just changed. A host can now add a seat,
 * take one named person off a table, resize their party and move them to
 * another table, all from the sheet on this screen. What a host still CANNOT do
 * is enlarge a table: the hold API is insert-and-delete only, and the delete
 * cancels every claim on its way out. So the messages below now point at the
 * seat a host can take back, and still never point at "make the table bigger".
 *
 * This file words the ADD path only. `dinnerClaimEdit.ts` words the edit path,
 * and the split is deliberate: `full` on add means "nobody was added", on a
 * resize means "stays at 3", on a move means "nothing moved". Same status,
 * three sentences, and one table cannot say all three.
 */
import { mutationErrorMessage } from './mutation-error';

export type DinnerNoteSeverity = 'success' | 'info' | 'warning' | 'error';

export interface DinnerClaimNote {
  severity: DinnerNoteSeverity;
  message: string;
  /**
   * The wire status behind the note, '' when the failure carried none (a
   * transport error, a 403, a controller-level 400). Callers use it to decide
   * whether anything moved; tests use it to pin the mapping.
   */
  status: string;
}

/** The subset of `DinnerClaimResult` this module reads, all of it optional on the wire. */
interface DinnerClaimBody {
  status?: unknown;
  availableSpots?: unknown;
  maxPartySize?: unknown;
  conflictVenueName?: unknown;
  claim?: { partySize?: unknown } | null;
}

/**
 * Statuses that mean the table changed and the hold list is now stale.
 * `released` and `moved` are here because the host edit path can answer either
 * one, and a stale claimed-count is exactly the number a host reads to a
 * restaurant.
 */
const WROTE_SOMETHING = new Set(['claimed', 'updated', 'released', 'moved']);

/** True when this note describes a seat that now exists. */
export function dinnerClaimChangedTheTable(note: DinnerClaimNote): boolean {
  return WROTE_SOMETHING.has(note.status);
}

function asBody(value: unknown): DinnerClaimBody | null {
  return value && typeof value === 'object' ? (value as DinnerClaimBody) : null;
}

function asCount(value: unknown): number | null {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function spots(n: number): string {
  return `${n} spot${n === 1 ? '' : 's'}`;
}

/**
 * The status-to-words table. Returns null when the body carries no status at
 * all, so the caller can fall back to the transport message rather than invent
 * a dinner explanation for a network failure.
 */
function describe(value: unknown, guestName: string): DinnerClaimNote | null {
  const body = asBody(value);
  const status = typeof body?.status === 'string' ? body.status : '';
  if (!status) return null;

  const who = guestName.trim() || 'That guest';
  const note = (severity: DinnerNoteSeverity, message: string): DinnerClaimNote =>
    ({ severity, message, status });

  switch (status) {
    case 'claimed':
      return note('success', `${who} has a seat at this table.`);

    /*
      Only reachable when the guest already had a claim here of a different
      size. Studio always asks for one seat, so in practice this means their
      party just SHRANK — but the old size is not on the wire, so the wording
      states what is known and warns about what follows from it rather than
      asserting a number nobody sent.
    */
    case 'updated': {
      const size = asCount(body?.claim?.partySize);
      return note(
        'warning',
        `${who} already had a seat here. Their party is now ${size ?? 1}` +
          ' — if it was bigger before, those spots went back on the table.',
      );
    }

    case 'already_claimed':
      return note('info', `${who} already has a seat at this table. Nothing changed.`);

    /*
      None of these four is reachable from the ADD path — they are the edit
      path's answers, worded properly in `dinnerClaimEdit.ts`. Mapped here
      anyway, and defensively: the endpoints now share one status vocabulary and
      one result shape, so a status can arrive on a route nobody expected it on,
      and an unmapped refusal is exactly how a real one becomes "that didn't
      save". Cheap insurance, and the alternative is a host reading a raw wire
      string in a restaurant.
    */
    case 'released':
      return note('warning', `${who} is off this table now. Nobody was added.`);

    case 'already_released':
      return note('info', `${who} is not at this table. Nothing changed.`);

    case 'moved':
      return note('warning', `${who} was moved to another table rather than added to this one.`);

    case 'linked_to_invitee':
      return note(
        'warning',
        `${who} is on the guest list, so their name comes from there — change it on the Guests` +
          ' tab. Nothing was saved here.',
      );

    /*
      THE refusal this module was written for. The venue name is the whole
      value: "already booked elsewhere" sends a host hunting through every
      other hold, and "that didn't save" sends them nowhere at all.

      It used to end by naming who could release the seat, because no host
      endpoint could. One now can — the sheet on this screen takes a named
      person off a named table — so the message ends by pointing at that table
      instead. A refusal that names the fix and the screen it lives on is the
      whole promise of this module.
    */
    case 'dinner_conflict': {
      const venue = typeof body?.conflictVenueName === 'string' && body.conflictVenueName.trim()
        ? body.conflictVenueName.trim()
        : '';
      return note(
        'warning',
        venue
          ? `${who} already has a seat at ${venue} that evening, so this one was not saved.` +
            ` One table per guest per night — open ${venue} and take their seat there first.`
          : `${who} already has a seat at another dinner that evening, and the API did not name it,` +
            ' so this one was not saved. One table per guest per night.',
      );
    }

    case 'full': {
      const left = asCount(body?.availableSpots) ?? 0;
      return note(
        'error',
        left > 0
          ? `Only ${spots(left)} left at this table — not enough for that party. Nobody was added.`
          /*
            Still not "raise its total spots": there is no update route on a
            dinner hold — the API has POST (insert-only) and DELETE and nothing
            else — and DELETE is a soft-cancel that cancels every claim on the
            table with it. Pointing a host at "make the table bigger" points them
            at the one button that empties the guest list they are trying to add
            to, and it does it on the night.

            The last clause is the half that changed. Taking a seat back used to
            be something only the guest could do; the sheet on this screen now
            does it, so the message ends by naming the move instead of naming
            somebody who is not reading it.
          */
          : 'This table is full. Nobody was added — and Studio cannot make room by enlarging it:' +
            ' a table’s spots cannot be raised once it exists, and deleting it to re-make it' +
            ' bigger cancels every claim on it. Take someone off this table first.',
      );
    }

    /*
      Unreachable from this screen today (Studio asks for one seat, and the cap
      is never below one), and mapped anyway: the endpoint can answer it, and an
      unmapped refusal is exactly how a real one becomes "that didn't save".
    */
    case 'party_too_large': {
      const max = asCount(body?.maxPartySize);
      return note(
        'error',
        (max === null
          ? 'That party is too large for this table.'
          : `This table takes parties of at most ${max}.`) +
          ' Add each extra guest under their own name — then the restaurant has a name for' +
          ' every seat instead of one name and a headcount.',
      );
    }

    case 'not_found':
      return note(
        'error',
        `Studio could not find this table or ${who} on this trip. The hold may have just been` +
          ' deleted — reload the page.',
      );

    default:
      return note(
        'error',
        `The API refused that with “${status}”, which Studio has no wording for. Nothing was saved.`,
      );
  }
}

/** Read a 2xx claim response. Success and no-op both come back here. */
export function dinnerClaimResultNote(result: unknown, guestName: string): DinnerClaimNote {
  return (
    describe(result, guestName) ?? {
      severity: 'warning',
      message: 'The API accepted that but did not say what it did. Reload to see the table.',
      status: '',
    }
  );
}

/**
 * Read a thrown claim. Prefers the dinner status; falls back to the shared
 * message helper, which is the right answer for the failures that are NOT
 * dinner outcomes — a 403 from a non-owner host, a 400 for a missing invitee id,
 * a dropped connection.
 */
export function dinnerClaimErrorNote(error: unknown, guestName: string): DinnerClaimNote {
  const body = (error as { response?: { data?: unknown } })?.response?.data;
  const described = describe(body, guestName);
  if (described) return described;

  const who = guestName.trim() || 'that guest';
  return {
    severity: 'error',
    message: mutationErrorMessage(error) || `Could not save a seat for ${who}.`,
    status: '',
  };
}
